// React
import { useContext } from "react"

// Components
import { BaseTable, Button, Modal } from "components"
import { FormSection } from "./FormSection"

// Contexts
import { UploadContext } from "../contexts"

// Form
import { FormProvider, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"

// Third-Party Libraries
import toast from "react-hot-toast"

// Types
import type { UploadType } from "../types"

// Utils
import { useApi, useToggle } from "utils"
import { type FormType, useUploadData, validationSchema } from "../utils"

export function SaveFile(params: {
  disabled: boolean
}): JSX.Element {
  // Hooks
  const form = useToggle(false)
  const preview = useToggle(false)
  const { handleSaveFileExcel } = useContext(UploadContext)
  const { data, assignData, reset } = useUploadData()

  return (
    <>
      <Button
        color="primary" 
        disabled={params.disabled}
        onClick={() => {
          handleSaveFileExcel().then(res => {
            if (res.identity_batch) {
              assignData(res)
              preview.toggle()
            }
          })
        }}
      >
        SAVE
      </Button>

      {preview.isActive && (
        <Modal
          isOpen
          title="DATA FORM EXCEL"
          closeModal={reset}
        >
          <Modal.Body>
            <div className="overflow-x-auto">
              <BaseTable>
                <thead className='bg-[#EEEEEE]'>
                  <tr>
                    <th>NO</th>
                    <th>CHQ</th>
                    <th>ACCOUNT</th>
                    <th>NAME</th>
                    <th>COA TYPE</th>
                    <th>LEVEL</th>
                    <th>HEADER</th>
                  </tr>
                </thead>

                <tbody>
                  {data.data.map((val, index) => (
                    <tr key={index} className="hover:bg-gray-100">
                      <td className="text-center">{index + 1}</td>
                      <td className="text-center">{val.CHQ}</td>
                      <td>{val.ACCOUNT}</td>
                      <td>{val.NAME}</td>
                      <td>{val.COA_TYPE}</td>
                      <td className="text-center">{val.LEVEL}</td>
                      <td className="text-center">{val.HEADER}</td>
                    </tr>
                  ))}
                </tbody>
              </BaseTable>
            </div>

            <section className="font-bold">
              <div className="mt-5">NB :</div>
              <div>The data comes from the excel file you sent, it has not been corrected according to Hawk standards, after saving please review it first in the upload file list</div>
            </section>
          </Modal.Body>

          <Modal.Footer className="text-end">
            <div className="flex justify-end">
              <div className="flex gap-3">
                <Button
                  color="error"
                  onClick={() => {
                    preview.toggle()
                    reset()
                  }}
                >
                  CANCEL
                </Button>

                <Button
                  color="primary"
                  onClick={() => {
                    preview.toggle()
                    form.toggle()
                  }}
                >
                  OK
                </Button>
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      )}

      {form.isActive && (
        <FormModal
          identity_batch={data.identity_batch}
          toggle={form.toggle}
        />
      )}
    </>
  )
}

function FormModal(params: {
  identity_batch: UploadType["identity_batch"]
  toggle: () => void
}): JSX.Element {
  // Vars
  const toggle = params.toggle

  // Hooks
  const api = useApi()
  const { onSuccess } = useContext(UploadContext)

  // Form
  const defaultValues: FormType = {
    description: "",
    file_name: ""
  }
  const onSubmit = (values: FormType) => {
    // Vars
    const value = {
      ...values,
      identity_batch: params.identity_batch
    }

    return new Promise<void>((resolve) => {
      toast.promise(
        api.post(
          `/import_coa/savefile`,
          value,
          { params: value }
        ),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.message ?? err.response.message
        }
      ).then(() => {
        toggle()
        onSuccess()
      }).catch(() => {
      }).finally(() => {
        resolve()
      })
    })
  }
  const methods = useForm<FormType>({
    defaultValues,
    resolver: yupResolver(validationSchema)
  })
  const { formState, handleSubmit } = methods

  return (
    <Modal isOpen title="PRODUCT IMPORT" closeModal={toggle}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <FormSection />
          </Modal.Body>

          <Modal.Footer className="text-end">
            <Button 
              type="submit" 
              color="primary" 
              loading={formState.isSubmitting ? "true" : undefined}
            >
              SAVE
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  )
}